import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import { EntityIdAndType, EntityTypeEnum, OrganisationMeta, OrganisationMetaDetail, WorkflowActions } from 'api'
import { createWorkflowOrganisationMetaDetailsSelector } from 'selectors/workflowSelectors'
import { canManageOrganisationMetaSelector } from 'selectors/permissionSelectors'
import { CorrelatedResponseCallback, EntityTypeIdRecord } from 'store/notification/notificationTypes'
import { Groupbox } from 'view/components/panels'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { AgGridWrapper } from 'view/components/datagrid'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { TradeSplitDialog } from 'view/routes/tradeSplit/TradeSplitDialog'
import { TradeSplitPanelColumnDefs, tradeSplitPanelGridOptions } from './TradeSplitPanelGridModel'
import { Locale, Translation } from 'locale'

export type TradeSplitPanelProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps {
  workflowId: string
  organisationId: string
}

interface StateProps {
  organisationMetaDetails: OrganisationMetaDetail[]
  canManageOrganisationMetas: boolean
}

interface DispatchProps {
  onUpdate: (payload: { updatedEntity: OrganisationMeta; workflowId: string } & EntityIdAndType) => unknown
  onAddNew: (workflowId: string, entityTypeEnum: EntityTypeEnum, correlatedResponseCallback?: CorrelatedResponseCallback) => unknown
}

const workflowIdSelector = (_state: {}, ownProps: TradeSplitPanelProps) => ownProps.workflowId

const mapStateToProps = createStructuredSelector({
  organisationMetaDetails: createWorkflowOrganisationMetaDetailsSelector(workflowIdSelector),
  canManageOrganisationMetas: canManageOrganisationMetaSelector
})

const mapDispatchToProps = {
  onUpdate: WorkflowActions.createEntityUpdateReferenceData,
  onAddNew: WorkflowActions.createAddToExistingWorkflow
}

const Wrapper = styled(Groupbox)`
  height: 450px;
`

const TradeSplitGridHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const TradeSplitPanelComponent: React.FunctionComponent<TradeSplitPanelProps> = props => {
  const { workflowId, organisationId, organisationMetaDetails, canManageOrganisationMetas, onUpdate, onAddNew, showModal } = props

  const noTradeSplitsPlaceholder = () => <div>{Locale.translate(Translation.SC_NO_TRADE_SPLITS)}</div>

  const handleNewTradeSplit = () => {
    const correlatedResponseCallback = (localWorkflowId: string, newlyAddedEntityIdAndTypes: EntityTypeIdRecord) => {
      return showModal(
        <TradeSplitDialog
          workflowId={localWorkflowId}
          entityId={newlyAddedEntityIdAndTypes[EntityTypeEnum.ORGANISATION_META]}
          organisationId={organisationId}
          updateOnly={true}
        />
      )
    }

    onAddNew(workflowId, EntityTypeEnum.ORGANISATION_META, correlatedResponseCallback)
  }

  const handleUpdateTradeSplit = (entityId: string, entityDetail: OrganisationMetaDetail, splitTrades: boolean) => {
    onUpdate({
      entityId,
      entityType: EntityTypeEnum.ORGANISATION_META,
      updatedEntity: {
        ...entityDetail.organisationMeta,
        splitTrades
      },
      workflowId
    })
  }

  return (
    <Wrapper>
      <TradeSplitGridHeader>
        {canManageOrganisationMetas && (
          <TextButton type={TextButtonType.Primary} onClick={handleNewTradeSplit}>
            {Locale.translate(Translation.TC_NEW_TRADE_SPLIT)}
          </TextButton>
        )}
      </TradeSplitGridHeader>
      <AgGridWrapper
        className="datagrid-borderless"
        gridOptions={tradeSplitPanelGridOptions}
        columnDefs={TradeSplitPanelColumnDefs}
        rowData={organisationMetaDetails}
        placeholder={noTradeSplitsPlaceholder}
        context={{
          workflowId,
          onUpdateTradeSplit: handleUpdateTradeSplit
        }}
      />
    </Wrapper>
  )
}

export const TradeSplitPanel = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(TradeSplitPanelComponent)
)
